///
/// EarthBrowser
///
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { DataProviderRegistry } from "@/data-providers/DataProviderRegistry"
import type { IDataProvider } from "@/data-providers/types"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { SimpleNode } from "@/earthbrowser/entities/SimpleNode/SimpleNode"
import { geographic } from "@/earthbrowser/utils/geographic"
import { appStore } from "@/redux/store"
import { logger } from "@/utils/LogDispatcher"
import type { Geometry, LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from "@shared-types/geojson"
import { ActivityBase } from "@stovokor/engine/ActivityBase"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { rgba } from "@stovokor/math/color"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"

/**
 * UserDatasetActivity - Manages rendering of CSV, Shapefile, and GeoJSON datasets
 *
 * This activity creates and manages rendering layers for each open dataset window.
 * Each dataset is rendered based on its GeoJSON geometry data from the DataProvider.
 */
export class UserDatasetActivity extends ActivityBase {
	private datasets = new Map<string, DatasetLayer>()
	private container = new NodeBase()
	private unsubscribeStore?: () => void
	private pendingWindows = new Set<string>()
	private eventListeners = new Map<string, (event: Event) => void>()

	constructor(context: EarthBrowserContext) {
		super(context)
		this.worldNodes = [this.container]
	}

	public start(): void {
		// Listen for window open/close events
		this.subscribeToWindowChanges()

		// Load any existing windows that already have data
		void this.loadExistingWindows()
	}

	/**
	 * Subscribe to Redux store for window changes
	 */
	private subscribeToWindowChanges(): void {
		let prevWindowIds: string[] = []

		this.unsubscribeStore = appStore.subscribe(() => {
			const state = appStore.getState().dialog
			const windows = state.datasetWindows

			// Get current window IDs for CSV, Shapefile, GeoJSON
			const currentWindowIds = Object.values(windows)
				.filter(w => w.windowType === 'csv' || w.windowType === 'shapefile' || w.windowType === 'geojson')
				.map(w => w.id)

			// Find new windows - listen for their completion events
			const newWindows = currentWindowIds.filter(id => !prevWindowIds.includes(id))
			for (const windowId of newWindows) {
				const windowState = windows[windowId]
				if (windowState) {
					this.listenForDatasetComplete(windowId, windowState.windowType!)
				}
			}

			// Find closed windows
			const closedWindows = prevWindowIds.filter(id => !currentWindowIds.includes(id))
			for (const windowId of closedWindows) {
				this.unloadDataset(windowId)
				this.removeEventListener(windowId)
			}

			// Update visibility for existing windows
			for (const windowId of currentWindowIds) {
				const windowState = windows[windowId]
				const dataset = this.datasets.get(windowId)
				if (windowState && dataset) {
					dataset.setVisible(windowState.isVisible)
				}
			}

			prevWindowIds = currentWindowIds
		})
	}

	/**
	 * Listen for the dataset:complete event for a specific window
	 */
	private listenForDatasetComplete(windowId: string, windowType: string): void {
		// Don't listen if already loaded or already listening
		if (this.datasets.has(windowId) || this.pendingWindows.has(windowId)) {
			return
		}

		this.pendingWindows.add(windowId)

		const handler = (_event: Event) => {
			// Data is now ready, load the dataset
			void this.loadDataset(windowId, windowType)
			this.removeEventListener(windowId)
		}

		this.eventListeners.set(windowId, handler)
		window.addEventListener(`dataset:complete:${windowId}`, handler)

		// Also try to load immediately in case data is already available
		// (e.g., for already-loaded datasets on page refresh)
		void this.tryLoadDataset(windowId, windowType)
	}

	/**
	 * Remove event listener for a window
	 */
	private removeEventListener(windowId: string): void {
		const handler = this.eventListeners.get(windowId)
		if (handler) {
			window.removeEventListener(`dataset:complete:${windowId}`, handler)
			this.eventListeners.delete(windowId)
		}
		this.pendingWindows.delete(windowId)
	}

	/**
	 * Load any existing dataset windows on startup
	 */
	private async loadExistingWindows(): Promise<void> {
		const state = appStore.getState().dialog
		const windows = Object.values(state.datasetWindows)
			.filter(w => w.windowType === 'csv' || w.windowType === 'shapefile' || w.windowType === 'geojson')

		for (const windowState of windows) {
			// Listen for completion event in case data is still loading
			this.listenForDatasetComplete(windowState.id, windowState.windowType!)
		}
	}

	/**
	 * Try to load a dataset if data is available, otherwise wait for event
	 */
	private async tryLoadDataset(windowId: string, windowType: string): Promise<void> {
		// Check if already loaded
		if (this.datasets.has(windowId)) {
			return
		}

		// Get the data provider
		const provider = DataProviderRegistry.get(windowId)
		if (!provider) {
			// No provider yet, wait for completion event
			return
		}

		// Check if provider has data
		try {
			const stats = await provider.getStats()
			if (stats.totalRows === 0) {
				// No data yet, wait for completion event
				return
			}

			// Data is available, load it
			await this.loadDataset(windowId, windowType)
		} catch {
			// Provider not ready, wait for completion event
			logger.warn(`Dataset ${windowId}: Provider not ready, waiting for completion event`)
		}
	}

	/**
	 * Load a dataset and create a rendering layer for it
	 */
	public async loadDataset(windowId: string, windowType: string): Promise<DatasetLayer | null> {
		try {
			// Check if already loaded
			if (this.datasets.has(windowId)) {
				return this.datasets.get(windowId)!
			}

			// Get the data provider
			const provider = DataProviderRegistry.get(windowId)
			if (!provider) {
				logger.warn(`Dataset ${windowId} has no provider`)
				return null
			}

			// Create dataset layer
			const context = this.context as EarthBrowserContext
			const dataset = new DatasetLayer(context, windowId, provider)
			await dataset.initialize()

			this.datasets.set(windowId, dataset)
			this.container.addNode(dataset)

			// Set initial visibility from window state
			const state = appStore.getState().dialog
			const windowState = state.datasetWindows[windowId]
			if (windowState) {
				dataset.setVisible(windowState.isVisible)
			}

			logger.info(`Loaded dataset layer: ${windowId} (${windowType})`)
			return dataset
		} catch (error) {
			logger.error(`Failed to load dataset ${windowId}:`, error)
			return null
		}
	}

	/**
	 * Unload a dataset layer
	 */
	public unloadDataset(windowId: string): void {
		const dataset = this.datasets.get(windowId)
		if (dataset) {
			this.container.removeNode(dataset)
			dataset.destroy()
			this.datasets.delete(windowId)
			logger.info(`Unloaded dataset layer: ${windowId}`)
		}
	}

	public override destroy(): void {
		// Unsubscribe from store
		if (this.unsubscribeStore) {
			this.unsubscribeStore()
			this.unsubscribeStore = undefined
		}

		// Clean up all event listeners
		for (const windowId of this.eventListeners.keys()) {
			this.removeEventListener(windowId)
		}

		// Clean up all datasets
		for (const dataset of this.datasets.values()) {
			dataset.destroy()
		}
		this.datasets.clear()
		super.destroy()
	}
}

/**
 * DatasetLayer - Renders a single dataset (CSV, Shapefile, or GeoJSON)
 *
 * Reads GeoJSON features from the DataProvider and creates visual representations.
 */
export class DatasetLayer extends NodeBase {
	public readonly windowId: string
	private context: EarthBrowserContext
	private provider: IDataProvider
	private initialized = false

	constructor(context: EarthBrowserContext, windowId: string, provider: IDataProvider) {
		super()

		this.context = context
		this.windowId = windowId
		this.provider = provider

	}

	/**
	 * Initialize the dataset by loading features and creating nodes
	 */
	public async initialize(): Promise<void> {
		if (this.initialized) return

		try {
			// Query all data from provider
			const result = await this.provider.query({
				offset: 0,
				limit: 10000, // Load a reasonable amount
			})

			// logger.info(`Dataset ${this.windowId}: Loaded ${result.data.length} rows`)
			const rows: Array<Record<string, unknown>> = result.rows
			if(rows.length === 0 || rows[0] === undefined) {
				logger.warn(`Dataset ${this.windowId}: No rows found`)
				return
			}

			const firstRow = rows[0]
			const keys = Object.keys(firstRow)

			logger.info(`Dataset ${this.windowId}: Found ${rows.length} rows, columns: ${keys.join(', ')}`)

			// Strategy 1: Look for geometry column FIRST (GeoJSON geometry object)
			// This takes priority because shapefiles/geojson may have both geometry AND lat/lon columns
			const geomKey = keys.find(k =>
				/^geometry$/i.test(k) || // exact match for 'geometry'
				/^geom$/i.test(k) || // exact match for 'geom'
				/the_geom/i.test(k) || // matches: the_geom
				/^shape$/i.test(k) // exact match for 'shape'
			)

			logger.info(`Dataset ${this.windowId}: geomKey=${geomKey}, firstRow.geometry type=${typeof firstRow?.geometry}`)

			if (geomKey) {
				let geometryCount = 0
				let createdCount = 0
				// Process each row and create feature nodes from geometry
				for (const row of rows) {
					if (row[geomKey]) {
						geometryCount++
						try {
							// Parse geometry if it's a string, using the found key
							let geometry: unknown = row[geomKey]
							if (typeof geometry === 'string') {
								try {
									geometry = JSON.parse(geometry) as unknown
								} catch (e) {
									logger.warn(`Failed to parse geometry JSON:`, e)
									continue
								}
							}

							const featureNode = this.createFeatureNode(geometry as Geometry)
							if (featureNode) {
								this.addNode(featureNode)
								createdCount++
							}
						} catch (error) {
							logger.warn(`Failed to create feature node:`, error)
						}
					}
				}
				logger.info(`Dataset ${this.windowId}: Found ${geometryCount} geometries, created ${createdCount} nodes`)
			} else {
				// Strategy 2: Fall back to lat/lon columns if no geometry column found
				const latKey = keys.find(k =>
					/lat/i.test(k) || // matches: lat, latitude, Lat, LAT, lat_deg, latitude_decimal, etc.
					/^y$/i.test(k) || // exact match for 'y' or 'Y'
					/^y[_-]/i.test(k) // matches: y_coord, y-coordinate, etc.
				)
				const lonKey = keys.find(k =>
					/lon/i.test(k) || // matches: lon, long, longitude, Lon, LON, lon_deg, etc.
					/^x$/i.test(k) || // exact match for 'x' or 'X'
					/^x[_-]/i.test(k) // matches: x_coord, x-coordinate, etc.
				)

				if (latKey && lonKey) {
					for (const row of rows) {
						// Coordinate columns hold numbers or numeric strings
						const latRaw = row[latKey] as string | number
						const lonRaw = row[lonKey] as string | number
						if (latRaw && lonRaw) {
							const lat = typeof latRaw === 'number' ? latRaw : parseFloat(String(latRaw))
							const lon = typeof lonRaw === 'number' ? lonRaw : parseFloat(String(lonRaw))
							if (isNaN(lat) || isNaN(lon)) {
								continue
							}
							const featureNode = this.createPointFeature({
								type: "Point",
								coordinates: [lon, lat]
							})
							if (featureNode) {
								this.addNode(featureNode)
							}
						}
					}
				} else {
					logger.warn(`Dataset ${this.windowId}: No geometry or lat/lon columns found`)
					return
				}
			}

			logger.info(`Dataset ${this.windowId}: Created ${this.children.length} feature nodes`)
			this.initialized = true
		} catch (error) {
			logger.error(`Failed to initialize dataset ${this.windowId}:`, error)
			// Don't throw - just log and continue with empty dataset
		}
	}

	/**
	 * Set the visibility of the entire dataset
	 */
	public setVisible(visible: boolean): void {
		this.visible = visible
	}


	private createFeatureNode(geometry: Geometry): NodeBase | undefined {

		try {
			// Validate geometry object
			if (!geometry || typeof geometry !== 'object') {
				logger.warn(`Invalid geometry object:`, geometry)
				return undefined
			}

			if (!geometry.type) {
				logger.warn(`Geometry missing type property:`, geometry)
				return undefined
			}

			// Create visualization based on geometry type
			switch (geometry.type) {
				case "Point":
					return this.createPointFeature(geometry)
				case "MultiPoint":
					return this.createMultiPointFeature(geometry)
				case "LineString":
					return this.createLineStringFeature(geometry)
				case "MultiLineString": {
					const result = this.createMultiLineStringFeature(geometry)
					if (!result) {
						logger.warn(`MultiLineString returned no nodes, coords length: ${(geometry).coordinates?.length}`)
					}
					return result
				}
				case "Polygon":
					return this.createPolygonFeature(geometry)
				case "MultiPolygon":
					return this.createMultiPolygonFeature(geometry)
				default:
					logger.warn(`Unsupported geometry type: ${geometry.type}`)
					return undefined
			}
		} catch (error) {
			logger.error("Failed to create feature node:", error)
			return undefined
		}
	}

	private createPointFeature(geometry: Point): NodeBase | undefined {
		if (!Array.isArray(geometry.coordinates) || geometry.coordinates.length < 2) {
			logger.warn(`Invalid Point coordinates:`, geometry.coordinates)
			return undefined
		}

		const [lon, lat] = geometry.coordinates
		if (typeof lon !== 'number' || typeof lat !== 'number') {
			logger.warn(`Invalid coordinate values:`, { lon, lat })
			return undefined
		}

		const position = new geographic(lon * trig.rad, lat * trig.rad, 1.0)

		const node = new SimpleNode(this.context, this.context.hexSphereGeometry)
		node.color = rgba(0.2, 0.8, 1.0, 1.0) // Cyan color
		node.transform = matrix.translate(position.cartesian).premultiply(matrix.scale(50.0))

		return node
	}

	private createMultiPointFeature(geometry: MultiPoint): NodeBase | undefined {
		if (!Array.isArray(geometry.coordinates)) {
			logger.warn(`Invalid MultiPoint coordinates:`, geometry.coordinates)
			return undefined
		}

		const nodes: NodeBase[] = []
		const context = this.context

		for (const coords of geometry.coordinates) {
			if (!Array.isArray(coords) || coords.length < 2) {
				continue
			}

			const [lon, lat] = coords
			if (typeof lon !== 'number' || typeof lat !== 'number') {
				continue
			}

			const position = new geographic(lon * trig.rad, lat * trig.rad, 1.0)

			const node = new SimpleNode(context, context.hexSphereGeometry)
			node.color = rgba(0.2, 0.8, 1.0, 1.0) // Cyan color
			node.transform = matrix.translate(position.cartesian).premultiply(matrix.scale(5.0))

			nodes.push(node)
		}

		if(nodes.length === 0) {
			return undefined
		}
		if(nodes.length === 1) {
			return nodes[0]
		}

		const node = new NodeBase()
		node.addNode(nodes)
		return node
	}

	private createLineStringFeature(geometry: LineString): NodeBase | undefined {
		if (!Array.isArray(geometry.coordinates) || geometry.coordinates.length < 2) {
			logger.warn(`Invalid LineString coordinates:`, geometry.coordinates)
			return undefined
		}

		const positions = geometry.coordinates
			.filter(coord => Array.isArray(coord) && coord.length >= 2)
			.map(([lon, lat]) => new geographic(lon * trig.rad, lat * trig.rad, 1.0))

		if (positions.length < 2) {
			logger.warn(`LineString has too few valid positions`)
			return undefined
		}

		const polyline = new PolylineNode(this.context, {
			positions: positions.map(p => p.cartesian),
			width: 2,
			color: rgba(0, 0.7, 1, 0.8), // Blue color for lines
			loop: false // Lines are not closed
		})

		return polyline
	}

	private createMultiLineStringFeature(geometry: MultiLineString): NodeBase | undefined {
		if (!Array.isArray(geometry.coordinates)) {
			logger.warn(`Invalid MultiLineString coordinates:`, geometry.coordinates)
			return
		}

		const nodes: NodeBase[] = []
		const context = this.context

		for (const lineCoords of geometry.coordinates) {
			if (!Array.isArray(lineCoords) || lineCoords.length < 2) {
				continue
			}

			const positions = lineCoords
				.filter(coord => Array.isArray(coord) && coord.length >= 2)
				.map(([lon, lat]) => new geographic(lon * trig.rad, lat * trig.rad, 1.0))

			if (positions.length < 2) {
				continue
			}

			const polyline = new PolylineNode(context, {
				positions: positions.map(p => p.cartesian),
				width: 2,
				color: rgba(0, 0.7, 1, 0.8), // Blue color for lines
				loop: false // Lines are not closed
			})

			nodes.push(polyline)
		}

		if(nodes.length === 0) {
			return undefined
		}
		if(nodes.length === 1) {
			return nodes[0]
		}

		const node = new NodeBase()
		node.addNode(nodes)
		return node
	}

	private createPolygonFeature(geometry: Polygon): NodeBase | undefined {
		if (!Array.isArray(geometry.coordinates) || geometry.coordinates.length === 0) {
			logger.warn(`Invalid Polygon coordinates:`, geometry.coordinates)
			return undefined
		}

		const nodes: NodeBase[] = []
		const context = this.context

		// Draw exterior ring and holes as polylines
		for (const ring of geometry.coordinates) {
			if (!Array.isArray(ring) || ring.length < 3) {
				logger.warn(`Invalid polygon ring (need at least 3 points):`, ring)
				continue
			}

			const positions = ring
				.filter(coord => Array.isArray(coord) && coord.length >= 2)
				.map(([lon, lat]) => new geographic(lon * trig.rad, lat * trig.rad, 1.0))

			if (positions.length < 3) {
				logger.warn(`Polygon ring has too few valid positions`)
				continue
			}

			const polyline = new PolylineNode(context, {
				positions: positions.map(p => p.cartesian),
				width: 2,
				color: rgba(1, 0, 0.5, 0.8), // Pink color for polygons
				loop: true // Close the polygon
			})

			nodes.push(polyline)
		}

		if(nodes.length === 0) {
			return undefined
		}
		if(nodes.length === 1) {
			return nodes[0]
		}

		const node = new NodeBase()
		node.addNode(nodes)
		return node
	}

	private createMultiPolygonFeature(geometry: MultiPolygon): NodeBase | undefined {
		if (!Array.isArray(geometry.coordinates)) {
			logger.warn(`Invalid MultiPolygon coordinates:`, geometry.coordinates)
			return undefined
		}

		const nodes: NodeBase[] = []
		const context = this.context

		for (const polygonCoords of geometry.coordinates) {
			if (!Array.isArray(polygonCoords)) {
				continue
			}

			for (const ring of polygonCoords) {
				if (!Array.isArray(ring) || ring.length < 3) {
					continue
				}

				const positions = ring
					.filter(coord => Array.isArray(coord) && coord.length >= 2)
					.map(([lon, lat]) => new geographic(lon * trig.rad, lat * trig.rad, 1.0))

				if (positions.length < 3) {
					continue
				}

				const polyline = new PolylineNode(context, {
					positions: positions.map(p => p.cartesian),
					width: 2,
					color: rgba(0.8, 0.2, 0.8, 1.0), // Purple color
					loop: true // Close the polygon
				})

				nodes.push(polyline)
			}
		}

		if(nodes.length === 0) {
			return undefined
		}
		if(nodes.length === 1) {
			return nodes[0]
		}

		const node = new NodeBase()
		node.addNode(nodes)
		return node
	}
}

